import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { LoadingSpinner } from '../../components/Molecules';
import { ClubAccessRestrictionPopup } from '../../components/Popups';
import { ClubListTemplate } from '../../components/Templates/Clubs';
import { SpacePrivacyPolicy } from '../../constants/space';
import {
  getSpacePageVariant,
  SpacePageType,
} from '../../constants/spacePageType';
import { usePopup } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import i18n, { TranslationLocale } from '../../i18n';
import { UiLocale } from '../../models/Base';
import { SpaceListType } from '../../models/SpaceStore';
import { WebBoosterType } from '../../models/UserStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getUiLocale } from '../../utils/i18n';

const { fetchHotSpaces } = useSpaceStore.getState();

const SEN_CLUB_ID = '7035ec9d-16c0-486e-892c-c76fd7c42d33';

interface ClubListScreenProps {
  spacePageType: SpacePageType;
}

const ClubListScreen: React.FC<ClubListScreenProps> = ({ spacePageType }) => {
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();

  const activeUserId = useSessionStore.getState().activeUserId;
  const userAccount = useUserStore((store) =>
    store.getAccount(activeUserId ?? ''),
  );

  useRedirectEntry();

  const [isLoading, setIsLoading] = useState(false);

  // MARK: Get Recent Classroom
  const recentSpaceField = useSpaceStoreListField(
    'spaces',
    SpaceListType.Recent,
  );

  const webBoosterType = userAccount?.settings.webBoosterType;
  const fetchRecentSpaces = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchRecentSpaces(webBoosterType, cursor),
    [webBoosterType],
  );

  const { fetchAll: fetchAllRecentSpaces } = useFetchList(
    recentSpaceField,
    fetchRecentSpaces,
  );

  const loadSpaces = useCallback(async () => {
    setIsLoading(true);
    switch (spacePageType) {
      case SpacePageType.Hot:
        await fetchHotSpaces({
          label:
            userAccount?.settings.webBoosterType === WebBoosterType.Normal
              ? 'trending'
              : 'learning',
          state: 'available',
        });
        break;
      case SpacePageType.Recent:
        await fetchAllRecentSpaces();
        break;
    }
    setIsLoading(false);
  }, [
    fetchAllRecentSpaces,
    spacePageType,
    userAccount?.settings.webBoosterType,
  ]);

  useEffect(() => {
    loadSpaces().catch(console.error);
  }, [loadSpaces]);

  const hotsSpaces = useSpaceStore((store) => {
    return store.getSpaces(SpaceListType.Hot);
  }, arrayEqual(shallow));

  const recentSpaces = useSpaceStore((store) => {
    return store.getSpaces(SpaceListType.Recent);
  }, arrayEqual(shallow));

  const spaces = useMemo(() => {
    switch (spacePageType) {
      case SpacePageType.Hot:
        return hotsSpaces;
      case SpacePageType.Recent:
        return recentSpaces.filter((item) => item.classroomId !== activeUserId); // designer say no include own club
    }
  }, [spacePageType, hotsSpaces, recentSpaces, activeUserId]);

  const onClubAccessRestricted = useCallback(
    (isSenClub?: boolean) => {
      if (isSenClub) {
        let link = '';
        switch (getUiLocale(i18n.language as TranslationLocale)) {
          case UiLocale.HK:
          case UiLocale.TW:
            link = 'https://joyajoy.com/zh-hant/pages/sen2024';
            break;
          case UiLocale.CN:
            link = 'https://joyajoy.com/zh-hans/pages/sen2024';
            break;
          case UiLocale.JP:
            link = 'https://joyajoy.com/pages/sen2024';
            break;
          default:
            link = 'https://joyajoy.com/pages/sen2024';
            break;
        }
        showPopup(
          <ClubAccessRestrictionPopup
            onDetails={() => {
              hidePopup();
              window.open(link);
            }}
            onCancel={hidePopup}
            type="senClub"
          />,
        );
      } else {
        showPopup(
          <ClubAccessRestrictionPopup
            onDetails={() => {
              hidePopup();
              navigate('/store');
              window.scrollTo(0, 0);
            }}
            onCancel={hidePopup}
            type="general"
          />,
        );
      }
    },
    [hidePopup, showPopup, navigate],
  );

  const onClickClub = useCallback(
    (classroomId: string) => {
      const classroom = useSpaceStore.getState().getSpace(classroomId ?? '');
      const classroomMembership = useSpaceStore
        .getState()
        .getActiveUserMembership(classroomId ?? '');
      if (
        classroom?.privacyPolicy === SpacePrivacyPolicy.Public ||
        (classroom?.privacyPolicy === SpacePrivacyPolicy.PrivateAndVisible &&
          classroomMembership?.isInGroup)
      ) {
        navigate(`/club/${classroomId}`);
      } else {
        onClubAccessRestricted(classroomId === SEN_CLUB_ID);
      }
    },
    [navigate, onClubAccessRestricted],
  );

  const onClickRecentAlbum = useCallback(() => {
    navigate(getSpacePageVariant(spacePageType).recentJourney);
  }, [navigate, spacePageType]);

  return (
    <>
      <ClubListTemplate
        title={getSpacePageVariant(spacePageType).name}
        spaces={spaces}
        webBoosterType={userAccount?.settings.webBoosterType}
        onClickClub={onClickClub}
        onClickRecentAlbum={onClickRecentAlbum}
      />
      {isLoading && <LoadingSpinner loading={isLoading} />}
    </>
  );
};

export default ClubListScreen;
